/**
 * AuthModal — the branded, on-brand auth UI that replaces the Cognito Hosted UI
 * for email/password. Driven by the custom-auth actions on the auth context
 * (SRP via CognitoAuthService). Views: sign-in, sign-up, confirm-email,
 * forgot-password, reset-password. "Continue with Google" still redirects
 * (federated IdP can't be a pure form).
 *
 * Sign-up carries an optional marketing-consent checkbox; on a successful
 * sign-up + confirm we record the choice via setEmailMarketingConsent so the
 * new user skips the separate /consent/email page.
 */
import { useEffect, useRef, useState } from 'react';
import { useAuthContext } from '../../contexts/AuthContext';
import { useUserProfileContext } from '../../contexts/UserProfileContext';
import { userProfileService } from '../../services/api/UserProfileService';
import type { AuthModalView } from '../../hooks/useAuth';
import './AuthModal.css';

// Federated Google sign-in requires a Google IdP on the Cognito pool — only
// the prod pool has one, so dev builds set VITE_GOOGLE_AUTH_ENABLED=false to
// hide the button (otherwise the Hosted UI errors with "Login option is not
// available"). Defaults ON so a missing var never hides it in prod.
const GOOGLE_AUTH_ENABLED = import.meta.env.VITE_GOOGLE_AUTH_ENABLED !== 'false';

export function AuthModal() {
  const {
    authModalOpen,
    authModalView,
    closeAuthModal,
    signInWithGoogle,
    submitSignIn,
    submitSignUp,
    submitConfirm,
    resendCode,
    submitForgot,
    submitReset,
  } = useAuthContext();
  const { refreshProfile } = useUserProfileContext();

  const [view, setView] = useState<AuthModalView>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  // Only record consent on confirm when we arrived here from sign-up (so an
  // unconfirmed-sign-in still falls through to the normal /consent/email page).
  const [recordConsentOnConfirm, setRecordConsentOnConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  // True only when the press began on the scrim itself — so a text-selection
  // drag that ends outside the dialog doesn't close the modal and wipe the form.
  const scrimMouseDownRef = useRef(false);

  // Sync the local view to the requested view each time the modal opens, and
  // clear transient/sensitive fields.
  useEffect(() => {
    if (authModalOpen) {
      setView(authModalView);
      setError(null);
      setInfo(null);
      setLoading(false);
      setPassword('');
      setCode('');
      setNewPassword('');
      setRecordConsentOnConfirm(false);
    }
  }, [authModalOpen, authModalView]);

  // While open: lock background scroll, and restore focus to the opener
  // (e.g. the Sign In button) when the modal closes.
  useEffect(() => {
    if (!authModalOpen) return;
    const opener = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
      opener?.focus?.();
    };
  }, [authModalOpen]);

  // Move focus to the first field whenever the modal opens or the view changes.
  // (Declared after the opener-capture effect above so that one still sees the
  // original focus target.)
  useEffect(() => {
    if (!authModalOpen) return;
    dialogRef.current?.querySelector<HTMLElement>('input')?.focus();
  }, [authModalOpen, view]);

  // Escape closes the modal; Tab is trapped inside the dialog.
  useEffect(() => {
    if (!authModalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeAuthModal();
        return;
      }
      if (e.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusables = dialog.querySelectorAll<HTMLElement>(
        'button:not(:disabled), input:not(:disabled), [href]'
      );
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !dialog.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [authModalOpen, closeAuthModal]);

  if (!authModalOpen) return null;

  const goto = (v: AuthModalView) => {
    setError(null);
    setInfo(null);
    setView(v);
  };

  /** Run an async action with shared loading/error handling. */
  const run = async (fn: () => Promise<void>) => {
    setError(null);
    setLoading(true);
    try {
      await fn();
    } catch (e) {
      setError((e as Error)?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const onSignIn = () =>
    run(async () => {
      try {
        await submitSignIn(email, password);
        closeAuthModal();
      } catch (e) {
        // An unverified account → send them to the confirm step instead of erroring.
        if ((e as { code?: string }).code === 'UserNotConfirmedException') {
          try {
            await resendCode(email);
          } catch {
            /* ignore resend failure; they can resend manually */
          }
          setInfo('Please verify your email — we sent you a 6-digit code.');
          setView('confirm');
          return;
        }
        throw e;
      }
    });

  const onSignUp = () =>
    run(async () => {
      const res = await submitSignUp(email, password, name);
      setRecordConsentOnConfirm(true);
      if (res.userConfirmed) {
        // Rare (no confirmation needed): sign in straight away.
        await submitSignIn(email, password);
        await recordConsent();
        closeAuthModal();
        return;
      }
      setInfo("We emailed you a 6-digit code — enter it to verify your account.");
      setView('confirm');
    });

  const onConfirm = () =>
    run(async () => {
      await submitConfirm(email, code, password);
      if (recordConsentOnConfirm) await recordConsent();
      closeAuthModal();
    });

  const onResend = () =>
    run(async () => {
      await resendCode(email);
      setInfo('A new code is on its way.');
    });

  const onForgot = () =>
    run(async () => {
      await submitForgot(email);
      setInfo('We emailed you a reset code.');
      setView('reset');
    });

  const onReset = () =>
    run(async () => {
      await submitReset(email, code, newPassword);
      closeAuthModal();
    });

  const onGoogle = () =>
    run(async () => {
      await signInWithGoogle(); // redirects away
    });

  /** Best-effort: record the marketing choice so the user skips /consent/email. */
  async function recordConsent() {
    try {
      await userProfileService.setEmailMarketingConsent(marketingConsent);
      await refreshProfile();
    } catch (err) {
      // Non-fatal — they'll just be prompted on the /consent/email page instead.
      console.warn('[AuthModal] Failed to record marketing consent:', err);
    }
  }

  const titles: Record<AuthModalView, string> = {
    signin: 'Welcome back',
    signup: 'Create your account',
    confirm: 'Verify your email',
    forgot: 'Reset your password',
    reset: 'Choose a new password',
  };

  return (
    <div
      className="auth-modal-scrim"
      onMouseDown={(e) => {
        scrimMouseDownRef.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (scrimMouseDownRef.current && e.target === e.currentTarget) closeAuthModal();
      }}
    >
      <div
        ref={dialogRef}
        className="auth-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="auth-modal-close" onClick={closeAuthModal} aria-label="Close">
          ✕
        </button>

        <div className="auth-modal-brand" aria-hidden="true">🎮 8-Bit Composer</div>
        <h2 id="auth-modal-title" className="auth-modal-title">{titles[view]}</h2>

        {error && <div className="auth-modal-error" role="alert">{error}</div>}
        {info && !error && <div className="auth-modal-info" role="status">{info}</div>}

        {/* ── Sign in ───────────────────────────────────────── */}
        {view === 'signin' && (
          <form onSubmit={(e) => { e.preventDefault(); onSignIn(); }}>
            <label className="auth-field">
              <span>Email</span>
              <input type="email" autoComplete="email" value={email} required
                onChange={(e) => setEmail(e.target.value)} />
            </label>
            <label className="auth-field">
              <span>Password</span>
              <PasswordInput autoComplete="current-password" value={password} onChange={setPassword} />
            </label>
            <button type="submit" className="auth-btn-primary" disabled={loading}>
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
            <button type="button" className="auth-link" onClick={() => goto('forgot')}>
              Forgot password?
            </button>
            {GOOGLE_AUTH_ENABLED && (
              <>
                <Divider />
                <GoogleButton onClick={onGoogle} disabled={loading} />
              </>
            )}
            <p className="auth-switch">
              New here?{' '}
              <button type="button" className="auth-link" onClick={() => goto('signup')}>
                Create an account
              </button>
            </p>
          </form>
        )}

        {/* ── Sign up ───────────────────────────────────────── */}
        {view === 'signup' && (
          <form onSubmit={(e) => { e.preventDefault(); onSignUp(); }}>
            <label className="auth-field">
              <span>Name</span>
              <input type="text" autoComplete="name" value={name} required
                onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="auth-field">
              <span>Email</span>
              <input type="email" autoComplete="email" value={email} required
                onChange={(e) => setEmail(e.target.value)} />
            </label>
            <label className="auth-field">
              <span>Password</span>
              <PasswordInput autoComplete="new-password" value={password} onChange={setPassword} />
              <small className="auth-hint">At least 8 characters with uppercase, lowercase, a number, and a symbol.</small>
            </label>
            <label className="auth-checkbox">
              <input type="checkbox" checked={marketingConsent}
                onChange={(e) => setMarketingConsent(e.target.checked)} />
              <span>Send me occasional tips, updates &amp; special offers (optional)</span>
            </label>
            <button type="submit" className="auth-btn-primary" disabled={loading}>
              {loading ? 'Creating…' : 'Create account'}
            </button>
            {GOOGLE_AUTH_ENABLED && (
              <>
                <Divider />
                <GoogleButton onClick={onGoogle} disabled={loading} label="Sign up with Google" />
              </>
            )}
            <p className="auth-switch">
              Already have an account?{' '}
              <button type="button" className="auth-link" onClick={() => goto('signin')}>
                Sign in
              </button>
            </p>
          </form>
        )}

        {/* ── Confirm email ─────────────────────────────────── */}
        {view === 'confirm' && (
          <form onSubmit={(e) => { e.preventDefault(); onConfirm(); }}>
            <p className="auth-subtle">Enter the 6-digit code we sent to <strong>{email}</strong>.</p>
            <label className="auth-field">
              <span>Verification code</span>
              <input type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                value={code} required onChange={(e) => setCode(e.target.value)} />
            </label>
            <button type="submit" className="auth-btn-primary" disabled={loading}>
              {loading ? 'Verifying…' : 'Verify & continue'}
            </button>
            <button type="button" className="auth-link" onClick={onResend} disabled={loading}>
              Resend code
            </button>
          </form>
        )}

        {/* ── Forgot password ───────────────────────────────── */}
        {view === 'forgot' && (
          <form onSubmit={(e) => { e.preventDefault(); onForgot(); }}>
            <p className="auth-subtle">Enter your email and we'll send a reset code.</p>
            <label className="auth-field">
              <span>Email</span>
              <input type="email" autoComplete="email" value={email} required
                onChange={(e) => setEmail(e.target.value)} />
            </label>
            <button type="submit" className="auth-btn-primary" disabled={loading}>
              {loading ? 'Sending…' : 'Send reset code'}
            </button>
            <button type="button" className="auth-link" onClick={() => goto('signin')}>
              Back to sign in
            </button>
          </form>
        )}

        {/* ── Reset password ────────────────────────────────── */}
        {view === 'reset' && (
          <form onSubmit={(e) => { e.preventDefault(); onReset(); }}>
            <label className="auth-field">
              <span>Reset code</span>
              <input type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                value={code} required onChange={(e) => setCode(e.target.value)} />
            </label>
            <label className="auth-field">
              <span>New password</span>
              <PasswordInput autoComplete="new-password" value={newPassword} onChange={setNewPassword} />
              <small className="auth-hint">At least 8 characters with uppercase, lowercase, a number, and a symbol.</small>
            </label>
            <button type="submit" className="auth-btn-primary" disabled={loading}>
              {loading ? 'Updating…' : 'Update password'}
            </button>
            <button type="button" className="auth-link" onClick={() => goto('signin')}>
              Back to sign in
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Divider() {
  return <div className="auth-divider"><span>or</span></div>;
}

/** Password field with a Show/Hide toggle (the pool's policy is strict — let users see what they typed). */
function PasswordInput({ value, onChange, autoComplete }: {
  value: string;
  onChange: (value: string) => void;
  autoComplete: 'current-password' | 'new-password';
}) {
  const [show, setShow] = useState(false);
  return (
    <div className="auth-password-wrap">
      <input
        type={show ? 'text' : 'password'}
        autoComplete={autoComplete}
        value={value}
        required
        onChange={(e) => onChange(e.target.value)}
      />
      <button
        type="button"
        className="auth-password-toggle"
        onClick={() => setShow((s) => !s)}
        aria-label={show ? 'Hide password' : 'Show password'}
      >
        {show ? 'Hide' : 'Show'}
      </button>
    </div>
  );
}

function GoogleButton({ onClick, disabled, label = 'Continue with Google' }: {
  onClick: () => void; disabled?: boolean; label?: string;
}) {
  return (
    <button type="button" className="auth-btn-google" onClick={onClick} disabled={disabled}>
      <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
        <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"/>
        <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"/>
        <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z"/>
        <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"/>
      </svg>
      {label}
    </button>
  );
}
